import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActions,
  Chip, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  TextField, Badge, Divider, List, ListItem, ListItemText, ListItemSecondaryAction,
  Alert, Menu, MenuItem, CircularProgress,
} from '@mui/material';
import {
  Add as AddIcon, Remove as RemoveIcon, Delete as DeleteIcon,
  Send as SendIcon, Receipt as ReceiptIcon, Restaurant as TableIcon,
  Kitchen as KitchenIcon, CheckCircle as CheckIcon,
  People as PeopleIcon,
  Search as SearchIcon, Clear as ClearIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { ordersApi } from '../api/orders.api';
import { tablesApi, TableZone, TABLE_ZONES } from '../api/tables.api';
import { recipesApi } from '../api/recipes.api';
import { stationsApi } from '../api/stations.api';
import { usersApi } from '../api/users.api';
import { useAuth } from '../context/AuthContext';
import { Station } from '../types';
import LoadingSpinner from '../components/common/LoadingSpinner';
import { getIgvLabel } from '../config/igv';

interface CartItem {
  recipeId: number;
  name: string;
  price: number;
  quantity: number;
  notes: string;
  // Estacion (bar / cocina) a la que va este item. Por defecto la del plato;
  // el mozo la puede cambiar antes de enviar. null = cocina por defecto.
  stationId: number | null;
}

const getApiErrorMessage = (err: any, fallback: string): string => {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join('. ');
  if (typeof message === 'string' && message.trim()) return message;
  return fallback;
};

const comandaReasonText = (reason?: string): string => {
  switch (reason) {
    case 'no-printer-configured':
      return 'sin impresora configurada';
    case 'printer-unreachable':
      return 'impresora no responde';
    case 'station-inactive':
      return 'estacion inactiva';
    case 'station-not-found':
      return 'estacion no encontrada';
    case 'ticket-sin-estacion':
      return 'items sin estacion asignada';
    default:
      return reason || 'error desconocido';
  }
};

const POS: React.FC = () => {
  const [tables, setTables] = useState<any[]>([]);
  const [recipes, setRecipes] = useState<any[]>([]);
  const [stations, setStations] = useState<Station[]>([]);
  // Menu para cambiar la estacion de un item del carrito.
  const [stationMenu, setStationMenu] = useState<{ anchor: HTMLElement; recipeId: number } | null>(null);
  const [activeOrders, setActiveOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState<any>(null);
  const [currentOrder, setCurrentOrder] = useState<any>(null);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [orderNotes, setOrderNotes] = useState('');
  const [viewOrderDialog, setViewOrderDialog] = useState(false);
  const [kitchenOrders, setKitchenOrders] = useState<any[]>([]);
  const [showKitchen, setShowKitchen] = useState(false);
  const [paidOrders, setPaidOrders] = useState<any[]>([]);
  const [showPaidDialog, setShowPaidDialog] = useState(false);
  const [zoneFilter, setZoneFilter] = useState<TableZone | 'todas'>('todas');
  const [categoryFilter, setCategoryFilter] = useState<string>('todas');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [categories, setCategories] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [closingAccount, setClosingAccount] = useState(false);
  // Envio del carrito en curso: bloquea el boton para no duplicar items/tickets.
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  // Preimpresion de comanda para cocina/bar (vista previa + confirmacion).
  const [comandaDialogOpen, setComandaDialogOpen] = useState(false);
  const [comandaLoading, setComandaLoading] = useState(false);
  const [comandaPrinting, setComandaPrinting] = useState(false);
  const [comandaPreview, setComandaPreview] = useState<any>(null);
  const [comandaError, setComandaError] = useState<string | null>(null);
  const [comandaResult, setComandaResult] = useState<string | null>(null);

  // Modal "cuantos comensales" antes de abrir una mesa libre. Si la mesa ya
  // tiene pedido activo no se pregunta (se trabaja sobre el order existente).
  const [pendingTable, setPendingTable] = useState<any>(null);
  const [pendingComensales, setPendingComensales] = useState(2);
  // Comensales del pedido que estamos editando (se manda al backend al crear).
  const [orderComensales, setOrderComensales] = useState<number | null>(null);

  // Modalidad "comanda al mozo" (ORDER_FLOW_MODE=mozo en el backend). En modo
  // tradicional estos estados no se usan y el POS se comporta como siempre.
  const { user } = useAuth();
  const [flowMode, setFlowMode] = useState<'tradicional' | 'mozo'>('tradicional');
  const isMozoMode = flowMode === 'mozo';
  const [mozos, setMozos] = useState<Array<{ id: number; fullName: string }>>([]);
  const [customerName, setCustomerName] = useState('');
  const [mozoId, setMozoId] = useState<number | ''>('');
  // Resultado de la impresion automatica al confirmar (se muestra al cajero).
  const [comandaConfirm, setComandaConfirm] = useState<{
    orderId: number;
    orderCode: string;
    mozoName?: string;
    printed: number;
    tickets: number;
    stations: Array<{ stationName: string; ok: boolean; reason?: string }>;
  } | null>(null);

  useEffect(() => {
    ordersApi.getFlowConfig()
      .then(res => {
        setFlowMode(res.data.mode);
        if (res.data.mode === 'mozo') {
          usersApi.getMozos().then(r => setMozos(r.data || [])).catch(err => console.error(err));
        }
      })
      .catch(() => setFlowMode('tradicional'));
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [tablesRes, recipesRes, activeRes, kitchenRes] = await Promise.all([
        tablesApi.getAll(),
        recipesApi.getAll(),
        ordersApi.getActive(),
        ordersApi.getKitchen(),
      ]);
      setTables(tablesRes.data);
      const activeRecipes = recipesRes.data.filter((r: any) => r.isActive !== false);
      setRecipes(activeRecipes);
      const uniqueCats = Array.from(new Set(activeRecipes.map((r: any) => r.category).filter(Boolean))) as string[];
      setCategories(uniqueCats.sort());
      setActiveOrders(activeRes.data);
      setKitchenOrders(kitchenRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  // Las estaciones cambian poco: se cargan una vez, aparte del auto-refresh.
  useEffect(() => {
    stationsApi.getAll()
      .then(res => setStations((res.data || []).filter(s => s.isActive !== false)))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh every 15 seconds
  useEffect(() => {
    const interval = setInterval(loadData, 15000);
    return () => clearInterval(interval);
  }, [loadData]);

  const getTableOrder = (tableId: number) =>
    activeOrders.find((o: any) => o.tableId === tableId);

  const addToCart = (recipe: any) => {
    const existing = cart.find(c => c.recipeId === recipe.id);
    if (existing) {
      setCart(cart.map(c => c.recipeId === recipe.id
        ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, {
        recipeId: recipe.id,
        name: recipe.name,
        price: Number(recipe.salePrice),
        quantity: 1,
        notes: '',
        stationId: recipe.stationId ?? null,
      }]);
    }
  };

  const updateCartQty = (recipeId: number, delta: number) => {
    setCart(cart.map(c => {
      if (c.recipeId !== recipeId) return c;
      const newQty = c.quantity + delta;
      return newQty > 0 ? { ...c, quantity: newQty } : c;
    }).filter(c => c.quantity > 0));
  };

  const removeFromCart = (recipeId: number) => {
    setCart(cart.filter(c => c.recipeId !== recipeId));
  };

  const cartTotal = cart.reduce((sum, c) => sum + c.price * c.quantity, 0);

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredRecipes = recipes.filter((r: any) => {
    const matchesCategory = categoryFilter === 'todas' || r.category === categoryFilter;
    const matchesName = !normalizedSearch || String(r.name || '').toLowerCase().includes(normalizedSearch);
    return matchesCategory && matchesName;
  });

  const handleSelectTable = (table: any) => {
    setSendError(null);
    const existingOrder = getTableOrder(table.id);
    if (existingOrder) {
      // Mesa ocupada: trabajar sobre el order existente. Recuperamos los
      // comensales que se cargaron al abrir la mesa.
      setSelectedTable(table);
      setCurrentOrder(existingOrder);
      setOrderComensales(existingOrder.comensales ?? null);
      setCart([]);
      return;
    }
    // Pedido rapido del mozo: abre la mesa directo, sin preguntar comensales.
    // Solo pone el nombre del cliente (para cocina) y envia.
    if (user?.role === 'mozo') {
      setSelectedTable(table);
      setCurrentOrder(null);
      setOrderComensales(null);
      setCart([]);
      setCustomerName('');
      setMozoId(user.id);
      return;
    }
    // Mesa libre: preguntar cantidad de comensales antes de abrir.
    const suggested = Math.max(1, Math.min(table.capacity || 2, 4));
    setPendingComensales(suggested);
    setPendingTable(table);
  };

  const confirmComensales = () => {
    if (!pendingTable) return;
    setSelectedTable(pendingTable);
    setCurrentOrder(null);
    setOrderComensales(pendingComensales);
    setCart([]);
    setPendingTable(null);
    // Nuevo pedido: limpiar cliente y proponer al usuario como mozo si es mozo.
    setCustomerName('');
    setMozoId(user?.role === 'mozo' ? user.id : '');
  };

  // Estacion elegida para un item del carrito (null = cocina por defecto).
  const getCartStation = (item: CartItem): Station | null =>
    stations.find(s => s.id === item.stationId)
    ?? recipes.find((r: any) => r.id === item.recipeId && r.station?.id === item.stationId)?.station
    ?? null;

  const changeCartStation = (recipeId: number, stationId: number) => {
    setCart(cart.map(c => (c.recipeId === recipeId ? { ...c, stationId } : c)));
    setStationMenu(null);
  };

  const refreshCurrentOrder = async () => {
    await loadData();
    const active = (await ordersApi.getActive()).data;
    const updated = active.find((o: any) => o.tableId === selectedTable.id);
    setCurrentOrder(updated || null);
    if (updated?.comensales) setOrderComensales(updated.comensales);
  };

  const handleSendOrder = async () => {
    if (!selectedTable || cart.length === 0 || sending) return;
    const isNewOrder = !currentOrder;
    if (isMozoMode && isNewOrder) {
      if (!customerName.trim()) { setSendError('Ingrese el nombre del cliente.'); return; }
      if (!mozoId) { setSendError('Seleccione el mozo que atendera la comanda.'); return; }
    }
    setSending(true);
    setSendError(null);
    try {
      const items = cart.map(c => ({
        recipeId: c.recipeId,
        quantity: c.quantity,
        notes: c.notes || undefined,
        // Estacion elegida por el mozo; si no hay, el backend usa la del plato.
        stationId: c.stationId ?? undefined,
      }));

      // La comanda se imprime UNA vez en el backend al confirmar; aqui solo se
      // muestra el resultado (no se vuelve a llamar a la impresion).
      const res = currentOrder
        ? await ordersApi.addItems(currentOrder.id, items)
        : await ordersApi.create({
          tableId: selectedTable.id,
          items,
          notes: orderNotes || undefined,
          comensales: orderComensales ?? undefined,
          customerName: customerName.trim() || undefined,
          ...(isMozoMode ? { mozoId: Number(mozoId) } : {}),
        } as any);
      const comanda = res?.data?.comanda;
      if (comanda) {
        setComandaConfirm({
          orderId: res.data.id,
          orderCode: res.data.code,
          mozoName: res.data.waiter?.fullName,
          printed: comanda.printed,
          tickets: comanda.tickets,
          stations: comanda.stations || [],
        });
      }
      setCart([]);
      setOrderNotes('');
      setCustomerName('');
      await refreshCurrentOrder();
    } catch (err: any) {
      console.error(err);
      setSendError(getApiErrorMessage(err, 'No se pudo enviar el pedido. Intente nuevamente.'));
      // 409: la mesa ya tenia pedido (pantalla desactualizada) o envio repetido.
      // Recargamos para trabajar sobre el pedido real y que el mozo vea lo ya enviado.
      if (err?.response?.status === 409) await refreshCurrentOrder().catch(() => {});
    } finally {
      setSending(false);
    }
  };

  const imprimirTicket = (order: any) => {
    const fecha = new Date().toLocaleString('es-PE');
    const items = order.items?.map((item: any) =>
      `<tr>
        <td style="text-align:left;">${item.quantity}x ${item.recipe?.name || 'Item'}</td>
        <td style="text-align:right;">S/ ${Number(item.subtotal).toFixed(2)}</td>
      </tr>`
    ).join('') || '';

    const html = `
      <html><head><title>Ticket</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        body { font-family: 'Courier New', monospace; font-size: 12px; width: 76mm; margin: 2mm; }
        .center { text-align: center; }
        .right { text-align: right; }
        .bold { font-weight: bold; }
        .line { border-top: 1px dashed #000; margin: 4px 0; }
        table { width: 100%; border-collapse: collapse; }
        td { padding: 2px 0; }
      </style></head><body>
        <div class="center bold" style="font-size:14px;">REST CAMPESTRE LA OLLA DE JUANITA EIRL</div>
        <div class="center">RUC: 20223351205</div>
        <div class="center" style="margin-top:4px;">TICKET DE VENTA</div>
        <div class="line"></div>
        <div>Pedido: ${order.code}</div>
        <div>Mesa: ${order.table?.name || 'Mesa ' + order.tableId}</div>
        ${order.customerDocument ? `<div>${order.customerDocType || 'Doc'}: ${order.customerDocument}</div>` : ''}
        ${order.customerName ? `<div>Cliente: ${order.customerName}</div>` : ''}
        <div>Fecha: ${fecha}</div>
        <div class="line"></div>
        <table>${items}</table>
        <div class="line"></div>
        <table>
          <tr><td>Subtotal:</td><td class="right">S/ ${Number(order.subtotal).toFixed(2)}</td></tr>
          <tr><td>${getIgvLabel()}:</td><td class="right">S/ ${Number(order.tax).toFixed(2)}</td></tr>
          <tr class="bold"><td style="font-size:14px;">TOTAL:</td><td class="right" style="font-size:14px;">S/ ${Number(order.total).toFixed(2)}</td></tr>
        </table>
        <div class="line"></div>
        <div class="center" style="margin-top:6px;">¡Gracias por su preferencia!</div>
        <div class="center">Vuelva pronto</div>
        <script>window.onload=function(){window.print();}</script>
      </body></html>`;

    const ventana = window.open('', '_blank', 'width=320,height=600');
    if (ventana) {
      ventana.document.write(html);
      ventana.document.close();
    }
  };

  const handlePreviewComanda = async (orderId: number) => {
    setComandaDialogOpen(true);
    setComandaLoading(true);
    setComandaError(null);
    setComandaResult(null);
    setComandaPreview(null);
    try {
      const res = await ordersApi.previewComanda(orderId);
      setComandaPreview(res.data);
    } catch (err: any) {
      setComandaError(getApiErrorMessage(err, 'No se pudo generar la vista previa.'));
    } finally {
      setComandaLoading(false);
    }
  };

  const handlePrintComanda = async () => {
    if (!comandaPreview || comandaPrinting) return;
    setComandaPrinting(true);
    setComandaError(null);
    setComandaResult(null);
    try {
      const res = await ordersApi.printComanda(comandaPreview.orderId);
      const results: any[] = res.data?.results || [];
      const printed = results.filter((r) => r.ok);
      const failed = results.filter((r) => !r.ok);
      if (failed.length === 0) {
        setComandaResult(`Comanda impresa en ${printed.length} estacion(es).`);
      } else {
        setComandaError(
          `${printed.length > 0 ? 'Impresion parcial. ' : ''}No se pudo imprimir en: ` +
            failed
              .map((f) => `${f.stationName} (${comandaReasonText(f.reason)})`)
              .join(', '),
        );
      }
    } catch (err: any) {
      setComandaError(getApiErrorMessage(err, 'No se pudo imprimir la comanda.'));
    } finally {
      setComandaPrinting(false);
    }
  };

  const handleCloseAccount = async (orderId: number) => {
    setActionError(null);
    setClosingAccount(true);
    try {
      await ordersApi.updateStatus(orderId, 'pagado');
      if (currentOrder) imprimirTicket(currentOrder);
      setViewOrderDialog(false);
      setCurrentOrder(null);
      setSelectedTable(null);
      loadData();
    } catch (err: any) {
      setActionError(
        getApiErrorMessage(
          err,
          'No se pudo cerrar la cuenta. Verifica que la caja esté abierta e intenta nuevamente.',
        ),
      );
    } finally {
      setClosingAccount(false);
    }
  };

  const handleUpdateItemStatus = async (itemId: number, status: string) => {
    try {
      await ordersApi.updateItemStatus(itemId, status);
      loadData();
    } catch (err) { console.error(err); }
  };

  const handleShowPaidOrders = async () => {
    try {
      const res = await ordersApi.getAll({ status: 'pagado' });
      setPaidOrders(res.data);
      setShowPaidDialog(true);
    } catch (err) { console.error(err); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">Panel POS</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<ReceiptIcon />} onClick={handleShowPaidOrders}>
            Pedidos Pagados
          </Button>
          <Button variant={showKitchen ? 'contained' : 'outlined'}
            startIcon={<KitchenIcon />}
            onClick={() => setShowKitchen(!showKitchen)}
            color={showKitchen ? 'warning' : 'primary'}>
            Cocina ({kitchenOrders.length})
          </Button>
        </Box>
      </Box>

      {showKitchen ? (
        /* Kitchen View */
        <Grid container spacing={2}>
          {kitchenOrders.map((order: any) => (
            <Grid size={{ xs: 12, md: 6, lg: 4 }} key={order.id}>
              <Card sx={{ border: '2px solid', borderColor: 'warning.main' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">{order.code}</Typography>
                    <Chip label={order.table?.name || `Mesa ${order.tableId}`} color="primary" size="small" />
                  </Box>
                  <Divider sx={{ mb: 1 }} />
                  {order.items?.map((item: any) => (
                    <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                      <Typography variant="body2">
                        {item.quantity}x {item.recipe?.name || `Receta ${item.recipeId}`}
                        {item.notes && <Typography variant="caption" color="text.secondary" display="block">Nota: {item.notes}</Typography>}
                      </Typography>
                      <Box>
                        {item.status === 'pendiente' && (
                          <Button size="small" color="warning"
                            onClick={() => handleUpdateItemStatus(item.id, 'preparando')}>
                            Preparar
                          </Button>
                        )}
                        {item.status === 'preparando' && (
                          <Button size="small" color="success"
                            onClick={() => handleUpdateItemStatus(item.id, 'listo')}>
                            Listo
                          </Button>
                        )}
                        {item.status === 'listo' && (
                          <Chip label="LISTO" color="success" size="small" />
                        )}
                      </Box>
                    </Box>
                  ))}
                </CardContent>
              </Card>
            </Grid>
          ))}
          {kitchenOrders.length === 0 && (
            <Grid size={12}>
              <Alert severity="info">No hay pedidos pendientes en cocina</Alert>
            </Grid>
          )}
        </Grid>
      ) : (
        /* POS View */
        <Grid container spacing={2}>
          {/* Tables */}
          <Grid size={{ xs: 12, md: 3 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Mesas</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                <Chip
                  label="Todas" size="small"
                  color={zoneFilter === 'todas' ? 'primary' : 'default'}
                  onClick={() => setZoneFilter('todas')}
                />
                {TABLE_ZONES.map((z) => (
                  <Chip
                    key={z.key} label={z.label} size="small"
                    color={zoneFilter === z.key ? 'primary' : 'default'}
                    onClick={() => setZoneFilter(z.key)}
                  />
                ))}
              </Box>
              <Grid container spacing={1}>
                {tables
                  .filter((t: any) => zoneFilter === 'todas' || t.zone === zoneFilter)
                  .map((table: any) => {
                  const hasOrder = !!getTableOrder(table.id);
                  const isSelected = selectedTable?.id === table.id;
                  return (
                    <Grid size={6} key={table.id}>
                      <Button fullWidth variant={isSelected ? 'contained' : 'outlined'}
                        color={hasOrder ? 'warning' : table.status === 'libre' ? 'success' : 'error'}
                        onClick={() => handleSelectTable(table)}
                        sx={{ py: 2, flexDirection: 'column' }}>
                        <TableIcon />
                        <Typography variant="caption">{table.name}</Typography>
                        {hasOrder && <Chip label="Ocupada" size="small" sx={{ mt: 0.5, fontSize: '0.65rem' }} />}
                      </Button>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          </Grid>

          {/* Menu */}
          <Grid size={{ xs: 12, md: 5 }}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Menu</Typography>
              {!selectedTable ? (
                <Alert severity="info">Selecciona una mesa para comenzar</Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Buscar plato..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ mb: 1.5 }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <Box sx={{ display: 'flex', alignItems: 'center', mr: 1, color: 'text.secondary' }}>
                            <SearchIcon fontSize="small" />
                          </Box>
                        ),
                        endAdornment: searchTerm ? (
                          <IconButton
                            size="small"
                            aria-label="Limpiar búsqueda"
                            onClick={() => setSearchTerm('')}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        ) : null,
                      },
                    }}
                  />
                  {categories.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
                      <Chip
                        label="Todas" size="small"
                        color={categoryFilter === 'todas' ? 'primary' : 'default'}
                        onClick={() => setCategoryFilter('todas')}
                      />
                      {categories.map((cat) => (
                        <Chip
                          key={cat} label={cat} size="small"
                          color={categoryFilter === cat ? 'primary' : 'default'}
                          onClick={() => setCategoryFilter(cat)}
                        />
                      ))}
                    </Box>
                  )}
                  <Grid container spacing={1}>
                    {filteredRecipes
                      .map((recipe: any) => (
                    <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={recipe.id}>
                      <Card
                        onClick={() => addToCart(recipe)}
                        sx={{
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          borderRadius: 2.5,
                          border: '1px solid',
                          borderColor: 'divider',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                          cursor: 'pointer',
                          transition: 'transform 0.15s ease, box-shadow 0.15s ease, border-color 0.15s ease',
                          '&:hover': {
                            transform: 'translateY(-3px)',
                            borderColor: '#7C3AED',
                            boxShadow: '0 8px 20px rgba(124,58,237,0.16)',
                          },
                          '&:active': { transform: 'translateY(-1px)' },
                        }}
                      >
                        <CardContent
                          sx={{
                            p: 1.75,
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            '&:last-child': { pb: 1.75 },
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{
                              lineHeight: 1.35,
                              minHeight: '2.7em',
                              mb: 1,
                              overflowWrap: 'anywhere',
                              wordBreak: 'break-word',
                            }}
                          >
                            {recipe.name}
                          </Typography>
                          <Box
                            sx={{
                              mt: 'auto',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 1,
                            }}
                          >
                            <Typography
                              sx={{
                                color: '#7C3AED',
                                fontWeight: 700,
                                fontSize: '1rem',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              S/ {Number(recipe.salePrice).toFixed(2)}
                            </Typography>
                            <IconButton
                              size="small"
                              aria-label={`Agregar ${recipe.name}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                addToCart(recipe);
                              }}
                              sx={{
                                flexShrink: 0,
                                width: 32,
                                height: 32,
                                bgcolor: 'rgba(124,58,237,0.10)',
                                color: '#7C3AED',
                                border: '1px solid',
                                borderColor: 'rgba(124,58,237,0.35)',
                                transition: 'background-color 0.15s ease, color 0.15s ease',
                                '&:hover': {
                                  bgcolor: '#7C3AED',
                                  color: '#fff',
                                  borderColor: '#7C3AED',
                                },
                              }}
                            >
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                  </Grid>
                  {filteredRecipes.length === 0 && (
                    <Alert severity="info" sx={{ mt: 1 }}>
                      No se encontraron platos con ese criterio.
                    </Alert>
                  )}
                </>
              )}
            </Paper>
          </Grid>

          {/* Cart & Current Order */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6">
                  {selectedTable ? selectedTable.name : 'Pedido'}
                </Typography>
                {currentOrder && (
                  <Button size="small" startIcon={<ReceiptIcon />}
                    onClick={() => { setActionError(null); setViewOrderDialog(true); }}>
                    Ver Cuenta ({currentOrder.code})
                  </Button>
                )}
              </Box>
              {selectedTable && orderComensales && (
                <Chip
                  size="small"
                  color="primary"
                  icon={<PeopleIcon />}
                  label={`${orderComensales} ${orderComensales === 1 ? 'persona' : 'personas'}`}
                  sx={{ mb: 1 }}
                />
              )}

              {cart.length === 0 && !currentOrder && (
                <Typography color="text.secondary" variant="body2">
                  Agrega platos del menu
                </Typography>
              )}

              {/* Existing order items */}
              {currentOrder && currentOrder.items?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary">Pedido actual:</Typography>
                  {(currentOrder.customerName || (isMozoMode && currentOrder.waiter?.fullName)) && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {currentOrder.customerName && <>Cliente: <b>{currentOrder.customerName}</b></>}
                      {isMozoMode && currentOrder.waiter?.fullName && <> · Mozo: <b>{currentOrder.waiter.fullName}</b></>}
                    </Typography>
                  )}
                  {currentOrder.items.map((item: any) => (
                    <Box key={item.id} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                      <Typography variant="body2">
                        {item.quantity}x {item.recipe?.name}
                      </Typography>
                      <Chip label={item.status} size="small"
                        color={item.status === 'listo' ? 'success' : item.status === 'preparando' ? 'warning' : 'default'} />
                    </Box>
                  ))}
                  <Divider sx={{ my: 1 }} />
                </Box>
              )}

              {/* New cart items */}
              {cart.length > 0 && (
                <>
                  <Typography variant="subtitle2" color="text.secondary">
                    {currentOrder ? 'Nuevos items:' : 'Pedido:'}
                  </Typography>
                  <List dense disablePadding>
                    {cart.map((item) => {
                      const station = getCartStation(item);
                      const isBar = station?.type === 'bar';
                      return (
                      <ListItem key={item.recipeId} disablePadding sx={{ py: 0.5 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={
                            <>
                              S/ {(item.price * item.quantity).toFixed(2)}
                              <Chip
                                component="span"
                                size="small"
                                label={`${station ? station.name : 'Cocina'} ▾`}
                                color={isBar ? 'info' : 'warning'}
                                variant="outlined"
                                title="Cambiar a que estacion se envia"
                                onClick={stations.length > 0
                                  ? (e) => setStationMenu({ anchor: e.currentTarget, recipeId: item.recipeId })
                                  : undefined}
                                sx={{ ml: 1, height: 18, fontSize: 11 }}
                              />
                            </>
                          }
                        />
                        <ListItemSecondaryAction>
                          <IconButton size="small" onClick={() => updateCartQty(item.recipeId, -1)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <Typography component="span" sx={{ mx: 1 }}>{item.quantity}</Typography>
                          <IconButton size="small" onClick={() => updateCartQty(item.recipeId, 1)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                          <IconButton size="small" color="error" onClick={() => removeFromCart(item.recipeId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                      );
                    })}
                  </List>

                  <Menu
                    anchorEl={stationMenu?.anchor}
                    open={!!stationMenu}
                    onClose={() => setStationMenu(null)}
                  >
                    <MenuItem disabled dense>Enviar a:</MenuItem>
                    {stations.map(s => {
                      const current = cart.find(c => c.recipeId === stationMenu?.recipeId)?.stationId;
                      return (
                        <MenuItem
                          key={s.id}
                          dense
                          selected={s.id === current}
                          onClick={() => stationMenu && changeCartStation(stationMenu.recipeId, s.id)}
                        >
                          {s.type === 'bar' ? '🍹' : '🍳'} {s.name}
                        </MenuItem>
                      );
                    })}
                  </Menu>

                  <Divider sx={{ my: 1 }} />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>Subtotal:</Typography>
                    <Typography variant="subtitle1" fontWeight={600}>
                      S/ {cartTotal.toFixed(2)}
                    </Typography>
                  </Box>

                  {/* Nombre del cliente al abrir el pedido: obligatorio en modo comanda
                      al mozo; opcional en tradicional (sale en cocina y en el ticket). */}
                  {!currentOrder && (
                    <TextField fullWidth size="small" required={isMozoMode}
                      label={isMozoMode ? 'Nombre del cliente' : 'Nombre del cliente (para cocina)'}
                      value={customerName} onChange={(e) => setCustomerName(e.target.value)}
                      inputProps={{ maxLength: 100 }} sx={{ mb: 1 }} />
                  )}
                  {/* Modo comanda al mozo: ademas se asigna el mozo que atendera */}
                  {isMozoMode && !currentOrder && (
                    <>
                      <TextField select fullWidth size="small" required label="Mozo que atendera"
                        value={mozoId} onChange={(e) => setMozoId(Number(e.target.value))} sx={{ mb: 1 }}
                        helperText={mozos.length === 0 ? 'No hay mozos activos registrados' : undefined}>
                        {mozos.map(m => (
                          <MenuItem key={m.id} value={m.id}>{m.fullName}</MenuItem>
                        ))}
                      </TextField>
                    </>
                  )}

                  <TextField fullWidth size="small" label="Notas" value={orderNotes}
                    onChange={(e) => setOrderNotes(e.target.value)} sx={{ mb: 1 }} />

                  {sendError && (
                    <Alert severity="error" sx={{ mb: 1 }} onClose={() => setSendError(null)}>
                      {sendError}
                    </Alert>
                  )}

                  <Button fullWidth variant="contained" color="primary"
                    startIcon={<SendIcon />} onClick={handleSendOrder} disabled={sending}>
                    {sending
                      ? 'Enviando...'
                      : isMozoMode
                        ? (currentOrder ? 'Agregar e imprimir comanda' : 'Confirmar pedido e imprimir comanda')
                        : (currentOrder ? 'Agregar a Pedido' : 'Enviar a Cocina/Bar')}
                  </Button>
                </>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* View Order Dialog */}
      <Dialog
        open={viewOrderDialog}
        onClose={() => { setViewOrderDialog(false); setActionError(null); }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cuenta - {currentOrder?.code}</DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}
          {currentOrder && (
            <>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Mesa: {currentOrder.table?.name || `Mesa ${currentOrder.tableId}`}
              </Typography>
              <List dense>
                {currentOrder.items?.map((item: any) => (
                  <ListItem key={item.id} disablePadding sx={{ py: 0.5 }}>
                    <ListItemText
                      primary={`${item.quantity}x ${item.recipe?.name || `Receta ${item.recipeId}`}`}
                      secondary={`S/ ${Number(item.subtotal).toFixed(2)}`}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>Subtotal:</Typography>
                <Typography>S/ {Number(currentOrder.subtotal).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography>{getIgvLabel()}:</Typography>
                <Typography>S/ {Number(currentOrder.tax).toFixed(2)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="h6" fontWeight={700}>Total:</Typography>
                <Typography variant="h6" fontWeight={700}>
                  S/ {Number(currentOrder.total).toFixed(2)}
                </Typography>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setViewOrderDialog(false); setActionError(null); }}>Cerrar</Button>
          <Button variant="outlined" startIcon={<PrintIcon />}
            onClick={() => currentOrder && handlePreviewComanda(currentOrder.id)}>
            Preimprimir Comanda
          </Button>
          <Button variant="outlined" startIcon={<ReceiptIcon />}
            onClick={() => currentOrder && imprimirTicket(currentOrder)}>
            Imprimir Ticket
          </Button>
          <Button variant="contained" color="success" startIcon={<CheckIcon />}
            disabled={closingAccount}
            onClick={() => currentOrder && handleCloseAccount(currentOrder.id)}>
            {closingAccount ? 'Cerrando...' : 'Cerrar Cuenta (Pagado)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preimpresion de comanda (vista previa + confirmacion) */}
      <Dialog
        open={comandaDialogOpen}
        onClose={() => !comandaPrinting && setComandaDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Preimprimir comanda{comandaPreview?.orderCode ? ` - ${comandaPreview.orderCode}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          {comandaError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setComandaError(null)}>
              {comandaError}
            </Alert>
          )}
          {comandaResult && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setComandaResult(null)}>
              {comandaResult}
            </Alert>
          )}

          {comandaLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            comandaPreview?.groups?.map((group: any, index: number) => {
              const isBar = group.stationType === 'bar';
              const isNone = group.stationType == null;
              return (
                <Paper key={`${group.stationId ?? 'none'}-${index}`} variant="outlined" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      px: 1.5,
                      py: 0.75,
                      bgcolor: isBar ? '#DBEAFE' : isNone ? '#F3F4F6' : '#FEF3C7',
                    }}
                  >
                    <Chip
                      size="small"
                      label={isBar ? 'BAR' : isNone ? 'SIN ESTACION' : 'COCINA'}
                      sx={{ fontWeight: 800 }}
                    />
                    <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
                      {group.stationName}
                    </Typography>
                    {!group.printerConfigured && (
                      <Typography variant="caption" color="error" fontWeight={700}>
                        Sin impresora
                      </Typography>
                    )}
                  </Box>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      p: 1.5,
                      fontSize: 11,
                      lineHeight: 1.5,
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    {group.content}
                  </Box>
                </Paper>
              );
            })
          )}

          {!comandaLoading && comandaPreview && !comandaPreview.groups?.some((g: any) => g.printerConfigured) && (
            <Alert severity="warning">
              Ninguna estacion tiene impresora configurada. Revisa Estaciones en el panel web.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setComandaDialogOpen(false)} disabled={comandaPrinting}>
            Cerrar
          </Button>
          <Button
            variant="contained"
            startIcon={<PrintIcon />}
            disabled={
              comandaLoading ||
              comandaPrinting ||
              !comandaPreview?.groups?.some((g: any) => g.printerConfigured)
            }
            onClick={handlePrintComanda}
          >
            {comandaPrinting ? 'Imprimiendo...' : 'Confirmar impresion'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Paid Orders Dialog */}
      <Dialog open={showPaidDialog} onClose={() => setShowPaidDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Pedidos Pagados</DialogTitle>
        <DialogContent>
          {paidOrders.length === 0 ? (
            <Alert severity="info">No hay pedidos pagados</Alert>
          ) : (
            <List>
              {paidOrders.map((order: any) => (
                <ListItem key={order.id} divider sx={{ py: 1.5 }}>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={700}>{order.code}</Typography>
                        <Chip label={order.table?.name || `Mesa ${order.tableId}`} size="small" color="primary" />
                        <Chip label="PAGADO" size="small" color="success" />
                      </Box>
                    }
                    secondary={
                      <Box sx={{ mt: 0.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {order.items?.map((i: any) => `${i.quantity}x ${i.recipe?.name || 'Item'}`).join(', ')}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                          Total: S/ {Number(order.total).toFixed(2)}
                        </Typography>
                      </Box>
                    }
                  />
                  <Button variant="outlined" startIcon={<ReceiptIcon />} size="small"
                    onClick={() => imprimirTicket(order)}>
                    Reimprimir
                  </Button>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPaidDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Modo mozo: resultado de la comanda impresa al confirmar (una sola impresion) */}
      <Dialog open={!!comandaConfirm} onClose={() => setComandaConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          {comandaConfirm && comandaConfirm.printed > 0 ? 'Comanda impresa' : 'Comanda no impresa'}
        </DialogTitle>
        <DialogContent>
          {comandaConfirm && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Pedido <b>{comandaConfirm.orderCode}</b>
                {comandaConfirm.mozoName && <> · Mozo: <b>{comandaConfirm.mozoName}</b></>}
              </Typography>
              {comandaConfirm.stations.map((s, i) => (
                <Alert key={i} severity={s.ok ? 'success' : 'warning'} sx={{ mb: 0.5, py: 0 }}>
                  {s.stationName}: {s.ok ? 'impresa' : comandaReasonText(s.reason)}
                </Alert>
              ))}
              {comandaConfirm.printed > 0 ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Entregue el ticket impreso al mozo para que atienda al cliente.
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  El pedido quedo registrado. Puede reintentar con "Preimprimir comanda".
                </Typography>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          {comandaConfirm && comandaConfirm.printed === 0 && (
            <Button onClick={() => {
              const id = comandaConfirm.orderId;
              setComandaConfirm(null);
              handlePreviewComanda(id);
            }}>
              Preimprimir comanda
            </Button>
          )}
          <Button variant="contained" onClick={() => setComandaConfirm(null)}>Entendido</Button>
        </DialogActions>
      </Dialog>

      {/* Modal "cuantos comensales" antes de abrir una mesa libre */}
      <Dialog open={!!pendingTable} onClose={() => setPendingTable(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <PeopleIcon sx={{ fontSize: 36, color: '#D97706' }} />
          <Typography variant="h6" sx={{ mt: 1 }}>
            {pendingTable?.name || `Mesa ${pendingTable?.number ?? ''}`}
          </Typography>
          <Typography variant="body2" sx={{ color: '#6B7280' }}>
            ¿Cuantos comensales?
          </Typography>
          {/* Capacidad max oculta temporalmente */}
        </DialogTitle>
        <DialogContent>
          {/* Counter grande */}
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 3, my: 2 }}>
            <IconButton
              color="primary" size="large"
              disabled={pendingComensales <= 1}
              onClick={() => setPendingComensales((c) => Math.max(1, c - 1))}
              sx={{ bgcolor: '#FEF3C7', '&:hover': { bgcolor: '#FDE68A' }, width: 56, height: 56 }}
            >
              <RemoveIcon sx={{ fontSize: 28 }} />
            </IconButton>
            <Typography sx={{ fontSize: 48, fontWeight: 900, minWidth: 70, textAlign: 'center' }}>
              {pendingComensales}
            </Typography>
            <IconButton
              color="primary" size="large"
              onClick={() => setPendingComensales((c) => c + 1)}
              sx={{ bgcolor: '#FEF3C7', '&:hover': { bgcolor: '#FDE68A' }, width: 56, height: 56 }}
            >
              <AddIcon sx={{ fontSize: 28 }} />
            </IconButton>
          </Box>

          {/* Atajos rapidos */}
          <Box sx={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            {[1, 2, 4, 6, 8].map((n) => (
              <Chip
                key={n}
                label={n}
                color={pendingComensales === n ? 'primary' : 'default'}
                onClick={() => setPendingComensales(n)}
                sx={{ fontWeight: 700, minWidth: 40 }}
              />
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPendingTable(null)}>Cancelar</Button>
          <Button
            variant="contained" color="warning"
            startIcon={<SendIcon />}
            onClick={confirmComensales}
          >
            Tomar pedido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default POS;
